import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  ArrowLeft,
  Award,
  Users,
  Calendar,
  Eye,
  ExternalLink,
  MessageCircle
} from 'lucide-react';

// Generate initials from company name
function getInitials(companyName) {
  if (!companyName) return '??';
  const words = companyName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Company Logo component with error fallback to initials
function CompanyLogo({ logo, companyName, size = 'lg', API }) {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    md: 'w-16 h-16 text-xl',
    lg: 'w-24 h-24 md:w-32 md:h-32 text-3xl md:text-4xl'
  };
  
  const initials = getInitials(companyName);
  
  // Show initials if no logo or if image failed to load
  if (!logo || imageError) {
    return (
      <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] flex items-center justify-center text-white font-bold shadow-lg`}>
        {initials}
      </div>
    );
  }
  
  // Build the logo URL - handle different cases
  let logoUrl = logo;
  if (logo.startsWith('http')) {
    // Full URL - use as is
    logoUrl = logo;
  } else if (logo.startsWith('/api/')) {
    // Already has /api prefix - use base URL without /api
    const baseUrl = API.replace(/\/api$/, '');
    logoUrl = `${baseUrl}${logo}`;
  } else if (logo.startsWith('/')) {
    // Relative path without /api - add API base
    logoUrl = `${API}${logo}`;
  } else {
    // No leading slash - add API base with slash
    logoUrl = `${API}/${logo}`;
  }
  
  return (
    <img 
      src={logoUrl} 
      alt={companyName}
      className={`${sizeClasses[size]} object-contain rounded-xl bg-white p-3`}
      onError={() => setImageError(true)}
    />
  );
}

export default function CompanyProfile({ categorySlug: propCategorySlug, companySlug: propCompanySlug }) {
  const params = useParams();
  const categorySlug = propCategorySlug || params.categorySlug;
  const companySlug = propCompanySlug || params.companySlug;
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/directory/company/${categorySlug}/${companySlug}`);
        setCompany(response.data);
      } catch (err) {
        console.error('Error fetching company:', err);
        setError('Entreprise non trouvée');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [API, categorySlug, companySlug]);

  const handleWhatsAppClick = () => {
    if (!company?.phone) return;
    
    let phone = company.phone.replace(/[^\d+]/g, '');
    if (phone.startsWith('0') && phone.length === 10) {
      phone = '+33' + phone.substring(1);
    }
    const cleanPhone = phone.replace(/\+/g, '');
    const message = encodeURIComponent(
      `Bonjour ${company.company_name}, je vous contacte depuis l'annuaire GEOTS Consulting.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    
    // Track click
    axios.post(`${API}/directory/professionals/${company.id}/track-click`).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{error || 'Entreprise non trouvée'}</h1>
          <Link to="/annuaire">
            <Button>Retour à l'annuaire</Button>
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = company.category_info;
  const familySlug = categoryInfo?.family_slug;

  // Schema.org LocalBusiness
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": company.company_name,
    "description": company.description_fr,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": company.city,
      "addressCountry": company.country,
      "postalCode": company.postal_code || ""
    },
    "telephone": company.phone || company.contact_phone,
    "email": company.email || company.contact_email,
    "url": company.website
  };

  return (
    <>
      <Helmet>
        <title>{company.company_name} - {company.city} | Annuaire GEOTS</title>
        <meta name="description" content={`${company.company_name} à ${company.city} (${company.country}). ${company.description_fr?.substring(0, 150)}...`} />
        <link rel="canonical" href={`https://geots.consulting/annuaire/${categorySlug}/${companySlug}`} />
        <meta property="og:title" content={`${company.company_name} - ${company.city}`} />
        <meta property="og:description" content={company.description_fr?.substring(0, 200)} />
        <meta property="og:type" content="business.business" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
              <Link to="/annuaire" className="hover:text-[#635BFF]">Annuaire</Link>
              <ChevronRight className="w-4 h-4" />
              {familySlug && (
                <>
                  <Link to={`/annuaire/${familySlug}`} className="hover:text-[#635BFF]">
                    {categoryInfo?.family_name_fr || 'Catégorie'}
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
              <Link to={`/annuaire/${familySlug}/${categorySlug}`} className="hover:text-[#635BFF]">
                {categoryInfo?.name_fr || categorySlug}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">{company.company_name}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className={`${company.featured ? 'bg-gradient-to-br from-[#0A2540] via-[#1a3a5c] to-[#0A2540]' : 'bg-gradient-to-br from-[#0A2540] to-[#1a3a5c]'} text-white`}>
          <div className="container mx-auto px-4 py-8 md:py-12">
            {/* Back link */}
            <Link 
              to={`/annuaire/${familySlug}/${categorySlug}`}
              className="inline-flex items-center gap-1 text-gray-300 hover:text-white mb-6 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à la liste
            </Link>

            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Logo avec initiales par défaut et fallback sur erreur */}
              <div className="flex-shrink-0">
                <CompanyLogo 
                  logo={company.logo} 
                  companyName={company.company_name} 
                  size="lg" 
                  API={API} 
                />
              </div>

              {/* Company Info */}
              <div className="flex-1">
                {company.featured && (
                  <span className="inline-block bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold mb-3">
                    ENTREPRISE PREMIUM
                  </span>
                )}
                
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                  {company.company_name}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-gray-300 mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {company.city}, {company.country}
                  </span>
                  {categoryInfo && (
                    <span className="flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full text-sm">
                      {categoryInfo.name_fr}
                    </span>
                  )}
                </div>

                {/* Quick contact buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                  {(company.phone || company.contact_phone) && (
                    <Button 
                      onClick={handleWhatsAppClick}
                      className="bg-[#25D366] hover:bg-[#20BD5A]"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  {(company.phone || company.contact_phone) && (
                    <a href={`tel:${company.phone || company.contact_phone}`}>
                      <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                        <Phone className="w-4 h-4 mr-2" />
                        Appeler
                      </Button>
                    </a>
                  )}
                  {(company.email || company.contact_email) && (
                    <a href={`mailto:${company.email || company.contact_email}`}>
                      <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="bg-white rounded-xl border p-6">
                <h2 className="text-xl font-bold text-[#0A2540] mb-4">Présentation</h2>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {company.description_fr || 'Aucune description disponible.'}
                </p>
              </div>

              {/* Services */}
              {company.services && company.services.length > 0 && (
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="text-xl font-bold text-[#0A2540] mb-4">Services proposés</h2>
                  <div className="flex flex-wrap gap-2">
                    {company.services.map((service, idx) => (
                      <span key={idx} className="bg-[#635BFF]/10 text-[#635BFF] px-3 py-1 rounded-full text-sm font-medium">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {company.certifications && company.certifications.length > 0 && (
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="text-xl font-bold text-[#0A2540] mb-4">Certifications & Qualifications</h2>
                  <div className="flex flex-wrap gap-3">
                    {company.certifications.map((cert, idx) => (
                      <span key={idx} className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium">
                        <Award className="w-4 h-4" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-white rounded-xl border p-6 sticky top-24">
                <h3 className="font-bold text-[#0A2540] mb-4">Coordonnées</h3>
                
                <div className="space-y-4">
                  {/* Address */}
                  {(company.address || company.city) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                      <div>
                        {company.address && <p className="text-gray-700">{company.address}</p>}
                        <p className="text-gray-700">
                          {company.postal_code && `${company.postal_code} `}{company.city}
                        </p>
                        <p className="text-gray-500">{company.country}</p>
                      </div>
                    </div>
                  )}

                  {/* Phone */}
                  {(company.phone || company.contact_phone) && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#635BFF]" />
                      <a 
                        href={`tel:${company.phone || company.contact_phone}`}
                        className="text-gray-700 hover:text-[#635BFF]"
                      >
                        {company.phone || company.contact_phone}
                      </a>
                    </div>
                  )}

                  {/* Email */}
                  {(company.email || company.contact_email) && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#635BFF]" />
                      <a 
                        href={`mailto:${company.email || company.contact_email}`}
                        className="text-gray-700 hover:text-[#635BFF] break-all"
                      >
                        {company.email || company.contact_email}
                      </a>
                    </div>
                  )}

                  {/* Website */}
                  {company.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-[#635BFF]" />
                      <a 
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-[#635BFF] flex items-center gap-1"
                      >
                        {company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  {(company.phone || company.contact_phone) && (
                    <Button 
                      onClick={handleWhatsAppClick}
                      className="w-full bg-[#25D366] hover:bg-[#20BD5A]"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contacter via WhatsApp
                    </Button>
                  )}
                  <Link to="/materiel/demande?type=autre" className="block">
                    <Button variant="outline" className="w-full">
                      Demander un devis
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {company.views || 0} vues
                  </span>
                  {company.company_size && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {company.company_size}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] py-12">
          <div className="container mx-auto px-4 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Vous aussi, inscrivez votre entreprise
            </h2>
            <p className="text-lg text-white/80 mb-6 max-w-xl mx-auto">
              Rejoignez l'annuaire GEOTS et gagnez en visibilité auprès des donneurs d'ordre.
            </p>
            <Link to="/directory/submit">
              <Button size="lg" className="bg-white text-[#635BFF] hover:bg-gray-100">
                Inscrire mon entreprise gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
